import React, { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { useLanguage } from "../context/LanguageContext";
import { Menu, X } from "lucide-react";

const NavLink = ({ href, label, onClick }) => (
  <a
    href={href}
    onClick={onClick}
    className="text-sm text-slate-400 hover:text-slate-50 transition-colors duration-200"
    data-testid={`nav-link-${href.replace("#", "")}`}
    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
  >
    {label}
  </a>
);

export const Navbar = () => {
  const { lang, toggleLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-950/80 backdrop-blur-xl border-b border-slate-800"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid="logo-link">
          <Logo />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink href="#services" label={t.nav.services} />
          <NavLink href="#value" label={t.nav.value} />
          <NavLink href="#process" label={t.nav.process} />
          <NavLink href="#about" label={t.nav.about} />
        </nav>

        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <button
            type="button"
            onClick={toggleLang}
            data-testid="lang-toggle-button"
            aria-label={t.common.langLabel}
            className="hidden sm:inline-flex items-center gap-1 px-1 py-1 rounded-sm border border-slate-800 bg-slate-900/40 hover:border-slate-700 transition-colors"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            <span
              className={`px-2 py-1 text-[11px] tracking-[0.15em] uppercase transition-colors ${
                lang === "es" ? "bg-slate-50 text-slate-950" : "text-slate-400"
              }`}
              data-testid="lang-toggle-es"
            >
              ES
            </span>
            <span
              className={`px-2 py-1 text-[11px] tracking-[0.15em] uppercase transition-colors ${
                lang === "en" ? "bg-slate-50 text-slate-950" : "text-slate-400"
              }`}
              data-testid="lang-toggle-en"
            >
              EN
            </span>
          </button>

          <a
            href="#cta"
            data-testid="header-cta-button"
            className="hidden md:inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-500 text-slate-50 text-sm font-medium rounded-sm transition-colors duration-200 shadow-[0_0_30px_rgba(37,99,235,0.25)]"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            {t.nav.cta}
          </a>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-sm border border-slate-800 text-slate-200 hover:bg-slate-900"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="md:hidden border-t border-slate-800 bg-slate-950/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            <NavLink href="#services" label={t.nav.services} onClick={closeMenu} />
            <NavLink href="#value" label={t.nav.value} onClick={closeMenu} />
            <NavLink href="#process" label={t.nav.process} onClick={closeMenu} />
            <NavLink href="#about" label={t.nav.about} onClick={closeMenu} />

            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={toggleLang}
                data-testid="lang-toggle-button-mobile"
                className="inline-flex items-center gap-1 px-1 py-1 rounded-sm border border-slate-800 bg-slate-900/40"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                <span
                  className={`px-2 py-1 text-[11px] tracking-[0.15em] uppercase ${
                    lang === "es" ? "bg-slate-50 text-slate-950" : "text-slate-400"
                  }`}
                >
                  ES
                </span>
                <span
                  className={`px-2 py-1 text-[11px] tracking-[0.15em] uppercase ${
                    lang === "en" ? "bg-slate-50 text-slate-950" : "text-slate-400"
                  }`}
                >
                  EN
                </span>
              </button>
              <a
                href="#cta"
                onClick={closeMenu}
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-500 text-slate-50 text-sm font-medium rounded-sm transition-colors"
                data-testid="mobile-cta-button"
              >
                {t.nav.cta}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
